import { useEffect, useRef, useState, type FormEvent } from "react";
import type { AdminTestimonial, TestimonialStatus } from "@tih/shared";
import {
  ApiError,
  adminCreateTestimonial,
  adminDeleteTestimonial,
  adminFetchTestimonials,
  adminModerateTestimonial,
} from "../../api/client";

const TABS: { key: TestimonialStatus | "ALL"; label: string }[] = [
  { key: "PENDING", label: "Pending" },
  { key: "PUBLISHED", label: "Published" },
  { key: "REJECTED", label: "Rejected" },
  { key: "ALL", label: "All" },
];

const EMPTY_FORM = {
  name: "",
  location: "",
  body: "",
  status: "PUBLISHED" as "PENDING" | "PUBLISHED",
};

export function AdminTestimonialsPage() {
  const formRef = useRef<HTMLElement>(null);
  const [tab, setTab] = useState<TestimonialStatus | "ALL">("PENDING");
  const [items, setItems] = useState<AdminTestimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const rows = await adminFetchTestimonials(tab === "ALL" ? undefined : tab);
      setItems(rows);
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  function startAdd() {
    setForm({ ...EMPTY_FORM });
    setShowAdd(true);
    setSuccess(null);
    setError(null);
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function cancelAdd() {
    setShowAdd(false);
    setForm({ ...EMPTY_FORM });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSuccess(null);

    const name = form.name.trim();
    const body = form.body.trim();
    if (!name) {
      setError("Name is required");
      setBusy(false);
      return;
    }
    if (body.length < 10) {
      setError("Story must be at least 10 characters");
      setBusy(false);
      return;
    }

    try {
      await adminCreateTestimonial({
        name,
        body,
        status: form.status,
        ...(form.location.trim() ? { location: form.location.trim() } : {}),
      });
      setSuccess(
        form.status === "PUBLISHED"
          ? "Testimony published on the site."
          : "Testimony saved as pending.",
      );
      cancelAdd();
      if (form.status === "PUBLISHED" && tab === "PENDING") {
        setTab("PUBLISHED");
      } else {
        await load();
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save testimony");
    } finally {
      setBusy(false);
    }
  }

  async function moderate(id: number, status: "PUBLISHED" | "REJECTED") {
    await adminModerateTestimonial(id, status);
    await load();
  }

  async function remove(id: number) {
    if (!confirm("Delete this testimony permanently?")) return;
    await adminDeleteTestimonial(id);
    await load();
  }

  return (
    <div className="admin-page">
      <header className="admin-page-header admin-page-header-row">
        <div>
          <h1>Testimonies</h1>
          <p>Approve visitor stories, add testimonies manually, or remove entries.</p>
        </div>
        {!showAdd && (
          <button type="button" className="btn btn-primary" onClick={startAdd}>
            + Add testimony
          </button>
        )}
      </header>

      {error && <div className="form-error">{error}</div>}
      {success && (
        <div className="form-success" style={{ padding: 12, marginBottom: 16 }}>
          {success}
        </div>
      )}

      {showAdd && (
        <section ref={formRef} className="admin-card" style={{ marginBottom: 24 }}>
          <h2 style={{ marginTop: 0, marginBottom: 16 }}>Add testimony</h2>
          <form onSubmit={onSubmit} className="admin-blog-form">
            <div className="admin-blog-form-fields">
              <label>
                <span>Name</span>
                <input
                  type="text"
                  maxLength={120}
                  value={form.name}
                  disabled={busy}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Sarah M."
                  required
                />
              </label>
              <label>
                <span>Location (optional)</span>
                <input
                  type="text"
                  maxLength={120}
                  value={form.location}
                  disabled={busy}
                  onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                  placeholder="e.g. Kampala · attended 3 nights"
                />
              </label>
              <label>
                <span>Story</span>
                <textarea
                  rows={5}
                  minLength={10}
                  maxLength={600}
                  value={form.body}
                  disabled={busy}
                  onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                  placeholder="A line or two on what God did in the room…"
                  required
                />
                <span className="form-help">{form.body.length} / 600</span>
              </label>
              <label>
                <span>Status</span>
                <select
                  value={form.status}
                  disabled={busy}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      status: e.target.value as "PENDING" | "PUBLISHED",
                    }))
                  }
                >
                  <option value="PUBLISHED">Published (live on site)</option>
                  <option value="PENDING">Pending (review later)</option>
                </select>
              </label>
            </div>
            <div className="admin-blog-form-actions">
              <button type="button" className="btn btn-ghost" disabled={busy} onClick={cancelAdd}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? "Saving…" : "Save testimony"}
              </button>
            </div>
          </form>
        </section>
      )}

      <div className="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={tab === t.key ? "active" : ""}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ opacity: 0.5 }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ opacity: 0.6 }}>Nothing here.</div>
      ) : (
        <ul className="admin-cards">
          {items.map((t) => (
            <li key={t.id} className="admin-card">
              {t.photoUrls.length > 0 && (
                <div className="testimonial-admin-photos">
                  {t.photoUrls.map((url) => (
                    <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                      <img src={url} alt="" loading="lazy" />
                    </a>
                  ))}
                </div>
              )}
              <blockquote>{t.body}</blockquote>
              <div className="admin-card-meta">
                <strong>{t.name}</strong>
                {t.location && <span>· {t.location}</span>}
                {t.authorEmail && <span>· {t.authorEmail}</span>}
                <span>· {new Date(t.createdAt).toLocaleString()}</span>
                <span className={`status-pill status-${t.status.toLowerCase()}`}>
                  {t.status}
                </span>
              </div>
              <div className="admin-card-actions">
                {t.status !== "PUBLISHED" && (
                  <button type="button" className="btn btn-primary" onClick={() => moderate(t.id, "PUBLISHED")}>
                    Approve
                  </button>
                )}
                {t.status !== "REJECTED" && (
                  <button type="button" className="btn btn-ghost" onClick={() => moderate(t.id, "REJECTED")}>
                    Reject
                  </button>
                )}
                <button type="button" className="btn btn-danger" onClick={() => remove(t.id)}>
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
